import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, Subscription } from 'rxjs';
import { VimeoService } from './vimeo.service';
import { ProductsService } from './products.service';

export type UploadPhase = 'idle' | 'uploading' | 'processing' | 'folder-adding' | 'saving' | 'completed' | 'failed' | 'canceled';

export interface UploadParams {
  file: File;
  title: string;
  description?: string;
  vimeoFolderUri?: string | null; // e.g. /users/xxxx/projects/{id}
  productFolderId: string; // Learnify folder/product id
}

export interface UploadState {
  active: boolean;
  phase: UploadPhase;
  progress: number; // 0..100
  loadedBytes: number;
  totalBytes: number;
  etaSeconds: number | null;
  speedBps: number | null;
  stepUploaded: boolean;
  stepAddedToFolder: boolean;
  stepSaved: boolean;
  startedAt: number | null;
  title?: string;
  description?: string;
  error?: string;
  vimeoVideoUri?: string;
  vimeoEmbedUrl?: string;
}

@Injectable({ providedIn: 'root' })
export class UploadManagerService {
  private stateSubject = new BehaviorSubject<UploadState>({
    active: false,
    phase: 'idle',
    progress: 0,
    loadedBytes: 0,
    totalBytes: 0,
    etaSeconds: null,
    speedBps: null,
    stepUploaded: false,
    stepAddedToFolder: false,
    stepSaved: false,
    startedAt: null,
  });
  state$ = this.stateSubject.asObservable();

  private currentSub: Subscription | null = null;

  constructor(
    private vimeoService: VimeoService,
    private productsService: ProductsService,
  ) {}

  get snapshot(): UploadState {
    return this.stateSubject.getValue();
  }

  startUpload(params: UploadParams): void {
    // Cancelar cualquier subida previa
    this.cancelUpload();

    const startedAt = Date.now();
    this.stateSubject.next({
      active: true,
      phase: 'uploading',
      progress: 0,
      loadedBytes: 0,
      totalBytes: params.file.size,
      etaSeconds: null,
      speedBps: null,
      stepUploaded: false,
      stepAddedToFolder: false,
      stepSaved: false,
      startedAt,
      title: params.title,
      description: params.description,
    });

    const sub = this.vimeoService.uploadVideoToVimeo(
      params.file,
      params.title,
      params.description || ''
    ).subscribe({
      next: (evt: any) => {
        if (evt?.type === 'progress') {
          const loaded = evt.loaded ?? 0;
          const total = params.file.size;
          const elapsedMs = Date.now() - startedAt;
          const speed = elapsedMs > 0 ? loaded / (elapsedMs / 1000) : null; // B/s
          const remaining = Math.max(total - loaded, 0);
          const eta = speed && speed > 0 ? Math.round(remaining / speed) : null;

          this.stateSubject.next({
            ...this.snapshot,
            active: true,
            phase: loaded >= total ? 'processing' : 'uploading',
            progress: Math.floor((loaded / total) * 100),
            loadedBytes: loaded,
            totalBytes: total,
            speedBps: speed,
            etaSeconds: eta,
          });
        } else if (evt?.type === 'complete') {
          // Subida finalizada en Vimeo
          const videoUri: string = evt.videoUri;
          const embedUrl: string = evt.embedUrl;

          this.stateSubject.next({
            ...this.snapshot,
            phase: 'processing',
            stepUploaded: true,
            vimeoVideoUri: videoUri,
            vimeoEmbedUrl: embedUrl,
            progress: 100,
            loadedBytes: params.file.size,
          });

          // Agregar a carpeta si hay folder seleccionado
          const doAddToFolder = (): Promise<void> => {
            if (!params.vimeoFolderUri) return Promise.resolve();
            const folderId = params.vimeoFolderUri.split('/').pop();
            if (!folderId) return Promise.resolve();

            this.stateSubject.next({ ...this.snapshot, phase: 'folder-adding' });
            return new Promise((resolve, reject) => {
              this.vimeoService.addVideoToFolder(folderId, videoUri).subscribe({
                next: () => {
                  this.stateSubject.next({ ...this.snapshot, stepAddedToFolder: true });
                  resolve();
                },
                error: (err) => {
                  // Continuar aunque falle
                  console.warn('addVideoToFolder falló:', err);
                  resolve();
                }
              });
            });
          };

          // Guardar en Learnify API
          const doSaveToLearnify = (): Promise<void> => {
            this.stateSubject.next({ ...this.snapshot, phase: 'saving' });

            const body = {
              Property: 'Videoteca',
              Value: embedUrl,
              ProductId: params.productFolderId,
              FileName: params.title,
              MetaData: JSON.stringify({
                platform: 'vimeo',
                videoUri: videoUri,
                folderUri: params.vimeoFolderUri || null,
                description: params.description || '',
                hasVimeoFolder: !!params.vimeoFolderUri,
                uploadDate: new Date().toISOString()
              })
            };

            return new Promise((resolve, reject) => {
              this.productsService.setProductInfo(body).subscribe({
                next: () => {
                  this.stateSubject.next({ ...this.snapshot, stepSaved: true, phase: 'completed' });
                  resolve();
                },
                error: (err) => {
                  this.stateSubject.next({ ...this.snapshot, phase: 'failed', error: 'Error al guardar en Learnify' });
                  reject(err);
                }
              });
            });
          };

          // Ejecutar en paralelo para acelerar post-procesado
          Promise.all([
            doAddToFolder().catch(() => {}), // no bloquear por fallo en carpeta
            doSaveToLearnify()
          ])
          .then(() => {
            // Asegurar estado final consistente
            const snap = this.snapshot;
            if (snap.phase !== 'failed') {
              this.stateSubject.next({ ...snap, phase: 'completed' });
            }
          })
          .catch(() => { /* El estado ya se marcó como failed si hubo error al guardar */ })
          .finally(() => {
            // mantener active=true hasta que el usuario lo cierre manualmente desde el widget
          });
        }
      },
      error: (err) => {
        this.stateSubject.next({ ...this.snapshot, phase: 'failed', active: true, error: 'Error de subida a Vimeo' });
      }
    });

    this.currentSub = sub;
  }

  cancelUpload(): void {
    if (this.currentSub) {
      this.currentSub.unsubscribe();
      this.currentSub = null;
    }
    const snap = this.snapshot;
    if (snap.active && snap.phase !== 'completed') {
      this.stateSubject.next({ ...snap, phase: 'canceled', active: false });
    }
  }

  clear(): void {
    // Ocultar el widget y resetear estado
    this.stateSubject.next({
      active: false,
      phase: 'idle',
      progress: 0,
      loadedBytes: 0,
      totalBytes: 0,
      etaSeconds: null,
      speedBps: null,
      stepUploaded: false,
      stepAddedToFolder: false,
      stepSaved: false,
      startedAt: null,
      error: undefined,
      vimeoVideoUri: undefined,
      vimeoEmbedUrl: undefined,
    });
  }
}
